import { createSelector } from 'reselect'

import { getInboxData } from 'common/billing/entities/addOns/selectors'
import errorTrackingService from 'utils/services/errorTrackingService'
import { AccountFlag } from 'utils/services/featureFlagsService/constants'

import { Ability } from '../constants/Ability'
import { AbilityState } from '../constants/AbilityState'
import { getAbilityDescriptor, getDefaultAbilitiesMap } from '../models/Ability/helpers'
import { AbilitiesMap } from '../models/Ability/schemas'

import { getAccountFlags, getCurrentAccount } from './appSelectors'

export const getAbilitiesRaw = createSelector(
  [getCurrentAccount],
  (currentAccount): Readonly<AbilitiesMap> => currentAccount?.abilities || getDefaultAbilitiesMap(),
)

export const getAgencyAnalyticsAbility = createSelector(
  getAccountFlags,
  (flags = {}): AbilitiesMap[Ability.AGENCY_ANALYTICS] => {
    if (flags?.[AccountFlag.ENABLE_LC_STATISTICS]) {
      return getAbilityDescriptor(Ability.AGENCY_ANALYTICS, AbilityState.ENABLED)
    }

    return getAbilityDescriptor(Ability.AGENCY_ANALYTICS, AbilityState.BLOCKED)
  },
)

export const getAutoAssignAbility = createSelector(
  getCurrentAccount,
  getInboxData,
  (account, inboxData): AbilitiesMap[Ability.AUTO_ASSIGN] => {
    if (inboxData.isEnabled || account?.is_auto_assign_available) {
      return getAbilityDescriptor(Ability.AUTO_ASSIGN, AbilityState.ENABLED)
    }
    if (inboxData.isAvailable) {
      return getAbilityDescriptor(Ability.AUTO_ASSIGN, AbilityState.LIMITED)
    }

    return getAbilityDescriptor(Ability.AUTO_ASSIGN, AbilityState.BLOCKED)
  },
)

export const getAIAlwaysOnAbility = createSelector(
  getAccountFlags,
  (flags = {}): AbilitiesMap[Ability.AI_ALWAYS_ON] =>
    getAbilityDescriptor(
      Ability.AI_ALWAYS_ON,
      flags?.[AccountFlag.ENABLE_AI_ALWAYS_ON_AUTOMATION]
        ? AbilityState.ENABLED
        : AbilityState.BLOCKED,
    ),
)

export const getLocallyCalculatedAbilities = createSelector(
  getAgencyAnalyticsAbility,
  getAutoAssignAbility,
  getAIAlwaysOnAbility,
  (agencyAnalytics, autoAssign, aiAlwaysOn): Partial<AbilitiesMap> => {
    return {
      [Ability.AGENCY_ANALYTICS]: agencyAnalytics,
      [Ability.AUTO_ASSIGN]: autoAssign,
      [Ability.AI_ALWAYS_ON]: aiAlwaysOn,
    }
  },
)

// We can extend this selected and add some calculated abilities here later
export const getAbilities = createSelector(
  getAbilitiesRaw,
  getLocallyCalculatedAbilities,
  (abilities, local): AbilitiesMap => ({
    ...local,
    ...abilities,
  }),
)

/**
 * Returns `true` if current account has requested ability
 *
 * @example
 * const hasSomeAbility = getHasAbility(state, Abilities.SOME_ABILITY)
 */
export const getHasAbility = createSelector(
  [getAbilities, (state: RootState, ability: Ability) => ability],
  (abilities, abilityName): boolean => abilities?.[abilityName]?.state === AbilityState.ENABLED,
)

/**
 * Accepts list of abilities that are needed to be checked.
 * Returns diff between connected abilities and passed abilities
 *
 * @example
 * userAbilities = [Ability.AI_KEYWORD]
 *
 * getUnavailableAbilities(state, [Ability.AI_KEYWORD, Ability.AI_AGENT]) // will return [Ability.AI_AGENT]
 */
export const getUnavailableAbilities = createSelector(
  [getAbilities, (state: RootState, abilitiesToCheck: Ability[]) => abilitiesToCheck],
  (abilities, abilitiesToCheck): Ability[] => {
    const unavailableAbilities = new Set<Ability>()
    abilitiesToCheck.forEach((ability) => {
      const isAvailable = Boolean(abilities?.[ability]?.state === AbilityState.ENABLED)
      if (!isAvailable) {
        unavailableAbilities.add(ability)
      }
    })

    return Array.from(unavailableAbilities)
  },
)

type GetAbilityReturnTypeBase<AbilityName extends Ability> = AbilitiesMap[AbilityName] & {
  /**
   * Indicates that the feature is fully enabled and accessible
   * for the current account.
   *
   * The feature’s UI elements should be visible and fully interactive,
   * allowing the user to utilize the feature without restrictions.
   */
  isEnabled: boolean

  /**
   * Indicates that the feature is available to the current account,
   * but not yet enabled.
   *
   * This state implies that the feature could be accessed by fulfilling
   * certain conditions (e.g., purchasing a product, upgrading the account’s
   * role within a team, acquiring billing permissions, etc.).
   *
   * In this state, the feature’s UI should be displayed to inform the user
   * of its availability, but all interactive functionality must be disabled
   * until the necessary conditions are met.
   */
  isLimited: boolean

  /**
   * Indicates that the feature is blocked and entirely inaccessible
   * to the current account.
   *
   * In this state, any UI elements associated with the feature must be
   * hidden, and all attempts to use the feature should be strictly prevented.
   *
   * Example: The feature is tied to a product, but that product itself is
   * unavailable to the user.
   */
  isBlocked: boolean
}

type GetAbilityReturnTypeEnabled<AbilityName extends Ability> =
  GetAbilityReturnTypeBase<AbilityName> & {
    state: AbilityState.ENABLED
    isEnabled: true
    isLimited: false
    isBlocked: false
  }

type GetAbilityReturnTypeLimited<AbilityName extends Ability> =
  GetAbilityReturnTypeBase<AbilityName> & {
    state: AbilityState.LIMITED
    isEnabled: false
    isLimited: true
    isBlocked: false
  }

type GetAbilityReturnTypeBlocked<AbilityName extends Ability> =
  GetAbilityReturnTypeBase<AbilityName> & {
    state: AbilityState.BLOCKED
    isEnabled: false
    isLimited: false
    isBlocked: true
  }

type GetAbilityReturnType<AbilityName extends Ability> =
  | GetAbilityReturnTypeEnabled<AbilityName>
  | GetAbilityReturnTypeLimited<AbilityName>
  | GetAbilityReturnTypeBlocked<AbilityName>

export const getAbility = createSelector(
  [getAbilities, <AbilityName extends Ability>(_: RootState, ability: AbilityName) => ability],
  <AbilityName extends Ability>(
    abilities: AbilitiesMap,
    ability: AbilityName,
  ): Readonly<GetAbilityReturnType<AbilityName>> => {
    let descriptor = abilities[ability]
    if (!descriptor) {
      errorTrackingService.logWarningOnce(`Request unknown ability ${ability}`, {
        scope: 'account',
        section: 'abilities',
        fingerprint: `Request unknown ability ${ability}`,
      })
      descriptor = getAbilityDescriptor(ability as Ability.USERS_TOTAL, AbilityState.LIMITED, {
        limit: 0,
        used: 0,
      }) as TSFixMe
    }
    const { state } = descriptor

    if (state === AbilityState.ENABLED) {
      return Object.freeze({
        ...descriptor,
        state,
        isEnabled: true,
        isLimited: false,
        isBlocked: false,
      })
    }

    if (state === AbilityState.LIMITED) {
      return Object.freeze({
        ...descriptor,
        state,
        isEnabled: false,
        isLimited: true,
        isBlocked: false,
      })
    }

    return Object.freeze({
      ...descriptor,
      state,
      isEnabled: false,
      isLimited: false,
      isBlocked: true,
    })
  },
  // there should be satisfies and it works,
  // but our test runner fails to interpret this keyword
) as <AbilityName extends Ability>(
  state: RootState,
  ability: AbilityName,
) => Readonly<GetAbilityReturnType<AbilityName>>
